import ProjectQuestionValidator from "Validators/Admin/ProjectQuestion";
import { useEffect, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";
import { useFormSchema } from "Hooks/useFormSchema";

import Button from "Components/Admin/Buttons";
import Input from "Components/Admin/Form/Input";
import Select from "Components/Admin/Form/Select";
import ToggleStatus from "Components/Admin/ToggleStatus";
import FormWrapper from "Components/Common/FormWrapper";
import GetIcon from "Components/Global/Icons";

import * as S from "./styles";

const TYPE_OPTIONS = [
  { value: "text", label: "Texto" },
  { value: "checkbox", label: "Múltipla Escolha" },
];

const QuestionForm = ({
  projectId,
  selectedQuestion,
  setSelectedQuestion,
  getProjectQuestions,
}) => {
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();

  const [selectedType, setSelectedType] = useState(null);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (!selectedQuestion || !selectedQuestion?.id) return;

    setSelectedType(
      TYPE_OPTIONS.find((o) => o.value === selectedQuestion?.type),
    );

    if (selectedQuestion?.type === "checkbox") {
      setOptions(selectedQuestion?.options);
    }
  }, [selectedQuestion]);

  const methods = useFormSchema(ProjectQuestionValidator, {
    defaultValues: {
      text: selectedQuestion?.text,
    },
  });

  const { register, reset } = methods;

  const submit = async (data) => {
    setLoading(true);

    try {
      const route = selectedQuestion?.id
        ? "/project-questions/update-project-question"
        : "/project-questions/create-project-question";

      data.project_question_id = selectedQuestion?.id;
      data.project_id = projectId;
      data.type = selectedType?.value;

      if (selectedType?.value === "checkbox") {
        data.options = options.map((option) => ({
          text: option.text,
        }));
      }

      await api.post(route, data);

      setLoading(false);

      showNotification(
        "success",
        "Sucesso!",
        "Pergunta atualizada com sucesso!",
      );

      reset();
      setSelectedQuestion(null);
      getProjectQuestions(projectId);
    } catch (error) {
      setLoading(false);
      return HTTPErrorCallback(error);
    }
  };

  return (
    <S.Wrapper>
      <S.Modal>
        <S.Section>
          <span>{selectedQuestion?.id ? "Atualizar" : "Cadastrar"}</span>
          <hr />
        </S.Section>

        <FormWrapper {...methods} submit={submit}>
          {selectedQuestion?.id ? (
            <S.Buttons>
              <div />

              <ToggleStatus
                route={"/project-questions/toggle-project-question-status/"}
                item={selectedQuestion}
                refresh={() => {
                  setSelectedQuestion(null);
                  getProjectQuestions(projectId);
                }}
              />
            </S.Buttons>
          ) : null}

          <Input
            {...register("text")}
            label="Texto"
            placeholder="Texto da Pergunta"
            required
            type="textarea"
            height="200px"
            disabled={selectedQuestion?.has_answer}
          />

          <Select
            name="type"
            label="Tipo da Pergunta"
            options={TYPE_OPTIONS}
            placeholder="Selecione um Tipo"
            value={selectedType}
            onChange={setSelectedType}
            maxWidth="100%"
            disabled={selectedQuestion?.has_answer}
          />

          {selectedType?.value === "checkbox"
            ? options.map((option, i) => (
                <S.OptionRow key={i}>
                  <Input
                    name={`options.${i}`}
                    label={`Opção ${i + 1}`}
                    placeholder="Digite aqui"
                    type="text"
                    value={option?.text}
                    onChange={(e) =>
                      setOptions(
                        options.map((o, y) =>
                          i === y ? { ...o, text: e.target.value } : o,
                        ),
                      )
                    }
                    disabled={selectedQuestion?.has_answer}
                  />

                  {!selectedQuestion?.has_answer ? (
                    <GetIcon
                      name="X"
                      onClick={() => {
                        setOptions(
                          options.slice(0, i).concat(options.slice(i + 1)),
                        );
                      }}
                    />
                  ) : null}
                </S.OptionRow>
              ))
            : null}

          {selectedType?.value === "checkbox" ? (
            <Button
              type="button"
              outline
              text="Adicionar Opção"
              onClick={() => {
                setOptions([...options, { text: "" }]);
              }}
              disabled={selectedQuestion?.has_answer}
            />
          ) : null}

          <S.Buttons>
            <Button
              type="button"
              outline
              text="Fechar"
              disabled={loading}
              onClick={() => setSelectedQuestion(null)}
            />

            <Button
              type="submit"
              text={selectedQuestion?.id ? "Atualizar" : "Cadastrar"}
              disabled={
                loading || !selectedType || selectedQuestion?.has_answer
              }
            />
          </S.Buttons>
        </FormWrapper>
      </S.Modal>
    </S.Wrapper>
  );
};

export default QuestionForm;
